// plan-modusign-style-acceptance §Phase2 — 서명 발송 상세 페이지.
// Route: /field/sign/documents/:id (FIELD_MANAGER + ADMIN)
//
// 개선 9건 차수1(26-08-04) §4 (결정 ③A) — 제조팀(MFG_MANAGER)도 들어온다.
// **읽기 전용 + PDF 다운로드**: 요청 취소·PDF 재생성·미리 채우기 편집은 서버
// WRITE_ROLES 밖이라 버튼을 렌더하지 않는다(열어 두면 403 만 돌아온다).
// 서명 링크는 서버가 응답에서 빼므로(canSeeSignUrl) '권한 없음'으로 표기된다.

import { useMemo, useState, type CSSProperties } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SIGNATURE_DOCUMENT_TYPE_LABELS,
  type SignatureField,
  type SignatureSignatoryStatus,
  type SignatureDocumentStatus,
} from '@bhops/shared';
import { signatureDocumentsApi } from '@/api/signatureDocuments';
import { extractApiError } from '@/api/client';
// 추가수정 2차(26-07-27) §2 — 표시 날짜·시각은 KST 정본으로.
import { kstDateTime, kstYmd } from '@/lib/format-date';
import { Button, Input } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { canEditField } from '@/permissions';
import { SignDocumentPrefillPanel } from './SignDocumentPrefillPanel';
// 개선 9건 차수1(26-08-04) §4 — 페이지 골라 받기 + 접근 규칙 정본.
import { SignDocumentPagePicker } from './SignDocumentPagePicker';
import { RobotMultiPicker, type PickedRobot } from './RobotMultiPicker';
import { canDownloadSignDocumentPdf, canWriteSignDocument } from './sign-access';

// sign-console-prefill-2026-07 — 운영자가 손으로 채우는 필드 = prefillable + literal.
const isEditablePrefill = (f: SignatureField): boolean =>
  (f.type === 'text' || f.type === 'date' || f.type === 'checkbox' || f.type === 'dropdown') &&
  f.binding.kind === 'literal';

const wrap: CSSProperties = { padding: '24px 28px', maxWidth: 960 };
const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  padding: '16px 20px',
  marginBottom: 20,
};
const sectionTitle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 14,
};
const kvRow: CSSProperties = {
  display: 'flex',
  gap: 8,
  marginBottom: 8,
  fontSize: 13,
};
const kvKey: CSSProperties = {
  width: 120,
  flexShrink: 0,
  color: 'var(--text-secondary)',
  fontWeight: 500,
};
const th: CSSProperties = {
  background: 'var(--bg-surface)',
  borderBottom: '1px solid var(--border-subtle)',
  padding: '9px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
};
const td: CSSProperties = {
  padding: '10px 14px',
  fontSize: 13,
  borderBottom: '1px solid var(--border-subtle)',
  verticalAlign: 'middle',
};
// sign-remote-link 트랙2(D3) — 서명자 인라인 편집 연필 버튼(라운드11 CRM
// 메모 타임라인의 crmIconBtn 관례).
const editIconBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--fg-muted)',
  padding: 2,
  flexShrink: 0,
  lineHeight: 0,
};

const DOC_STATUS_LABEL: Record<SignatureDocumentStatus, string> = {
  DRAFT: '초안',
  IN_PROGRESS: '진행 중',
  COMPLETED: '완료',
  CANCELLED: '취소',
};

const SIG_STATUS_LABEL: Record<SignatureSignatoryStatus, string> = {
  PENDING: '대기',
  OPENED: '열람함',
  SIGNED: '서명 완료',
  DECLINED: '거부',
};

function sigStatusPill(status: SignatureSignatoryStatus) {
  const colors: Record<SignatureSignatoryStatus, { bg: string; color: string }> = {
    PENDING: { bg: 'var(--bg-sunken)', color: 'var(--fg-muted)' },
    OPENED: { bg: 'var(--bg-warning-subtle)', color: 'var(--fg-on-warning-tint)' },
    SIGNED: { bg: 'var(--bg-success-subtle)', color: 'var(--fg-on-success-tint)' },
    DECLINED: { bg: 'var(--bg-danger-subtle)', color: 'var(--fg-danger)' },
  };
  const c = colors[status];
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        borderRadius: 'var(--r-pill)',
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 500,
      }}
    >
      {SIG_STATUS_LABEL[status]}
    </span>
  );
}

function docStatusPill(status: SignatureDocumentStatus) {
  const colors: Record<SignatureDocumentStatus, { bg: string; color: string }> = {
    DRAFT: { bg: 'var(--bg-sunken)', color: 'var(--fg-muted)' },
    IN_PROGRESS: { bg: 'var(--bg-info-subtle)', color: 'var(--fg-brand)' },
    COMPLETED: { bg: 'var(--bg-success-subtle)', color: 'var(--fg-on-success-tint)' },
    CANCELLED: { bg: 'var(--bg-danger-subtle)', color: 'var(--fg-danger)' },
  };
  const c = colors[status];
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        borderRadius: 'var(--r-pill)',
        padding: '3px 10px',
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {DOC_STATUS_LABEL[status]}
    </span>
  );
}

export function SignDocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const myRole = useAuthStore((s) => s.user?.role);
  const [cancelTarget, setCancelTarget] = useState<{ id: string } | null>(null);
  // sign-remote-link 트랙2(D3) — 서명자 이름·전화 인라인 편집 상태.
  const [editTarget, setEditTarget] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  // sign-console-prefill-2026-07 — 미리 채우기 오버레이 열림 상태.
  const [prefillOpen, setPrefillOpen] = useState(false);
  // 개선 9건 차수1(26-08-04) §4 — 페이지 골라 받기 패널 열림 상태(진행 중/완료 각각).
  const [pagePickerOpen, setPagePickerOpen] = useState(false);

  const q = useQuery({
    queryKey: ['signature-document', id],
    queryFn: () => signatureDocumentsApi.get(id!),
    enabled: !!id,
  });

  // ── 로봇 수정(2026-09-23 오너 요청) — 로봇 없이 만든 문서에 로봇을 정하거나 바꾼다 ──
  const [robotEdit, setRobotEdit] = useState<PickedRobot[] | null>(null);
  const robotsMutation = useMutation({
    mutationFn: (robotIds: string[]) => signatureDocumentsApi.updateRobots(id!, robotIds),
    onSuccess: () => {
      toast.success('로봇을 바꿨습니다.');
      setRobotEdit(null);
      qc.invalidateQueries({ queryKey: ['signature-document', id] });
      qc.invalidateQueries({ queryKey: ['signature-documents'] });
    },
    onError: (err) => toast.error(extractApiError(err), '로봇 수정 실패'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => signatureDocumentsApi.cancel(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-document', id] });
      qc.invalidateQueries({ queryKey: ['signature-documents'] });
      toast.success('서명 요청이 취소되었습니다.');
      setCancelTarget(null);
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // sign-remote-link 트랙2(D3) — 서명자 이름·전화 편집. 서버는 문서 IN_PROGRESS
  // + 해당 서명자 미서명(PENDING|OPENED)일 때만 허용(아니면 400).
  const updateSignatoryMutation = useMutation({
    mutationFn: (vars: { signatoryId: string; name: string; phone: string }) =>
      signatureDocumentsApi.updateSignatory(id!, vars.signatoryId, {
        name: vars.name,
        phone: vars.phone,
      }),
    onSuccess: () => {
      setEditTarget(null);
      qc.invalidateQueries({ queryKey: ['signature-document', id] });
      toast.success('서명자 정보가 저장되었습니다.');
    },
    onError: (err) => toast.error(extractApiError(err), '서명자 정보 저장 실패'),
  });

  // offline-signing PR-5 (plan D7) — finalize 안전망. COMPLETED 인데 최종
  // PDF 누락(과거 폰트 ENOENT 결함 등)·인수증/캐스케이드 미실행을 멱등 재처리.
  const refinalizeMutation = useMutation({
    mutationFn: () => signatureDocumentsApi.refinalize(id!),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['signature-document', id] });
      toast.success(
        r.finalPdfS3Key ? '최종 PDF를 다시 만들었습니다.' : '후처리를 다시 마쳤습니다.',
        'PDF 재생성',
      );
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // 추가수정 2차(26-07-27) §4 (결정 ⑤A) — "미리 채운 PDF 다운로드". 최종본과 같은
  // 합성기로 문서 전체를 PDF 한 개로 받는다(서명칸 빈 상태 · 인증 페이지 없음 ·
  // 빈 날짜 칸 자동 채움 없음). 진행 중 문서만 — 서버도 완료/취소는 400.
  const previewPdfMutation = useMutation({
    mutationFn: (fallbackName: string) =>
      signatureDocumentsApi.downloadPrefillPreview(id!, fallbackName),
    onError: (err) => toast.error(extractApiError(err), '미리 채운 PDF 다운로드 실패'),
  });

  // sign-console-prefill — 직접 입력 가능한 필드 + 저장된 값의 채움 수(요약 표시).
  const editableFields = useMemo(
    () => (q.data?.fieldsJson ?? []).filter(isEditablePrefill),
    [q.data?.fieldsJson],
  );
  const prefilledFilled = useMemo(() => {
    const pv = q.data?.prefilledValues ?? {};
    // 미체크 체크박스('false')는 채움으로 세지 않는다(패널 hasValue 와 동일 규칙).
    return editableFields.filter((f) => {
      const v = pv[f.id] ?? '';
      return f.type === 'checkbox' ? v === 'true' : v.trim().length > 0;
    }).length;
  }, [editableFields, q.data?.prefilledValues]);

  if (q.isLoading)
    return <div style={{ padding: 32, color: 'var(--text-secondary)' }}>불러오는 중…</div>;
  if (q.isError)
    return (
      <div style={{ padding: 32, color: 'var(--color-error)' }}>{extractApiError(q.error)}</div>
    );

  const doc = q.data;
  if (!doc) return null;

  const isActive = doc.status === 'IN_PROGRESS' || doc.status === 'DRAFT';

  // sign-remote-link 트랙2(D3) — PATCH 권한(ADMIN/FIELD_MANAGER/MANAGER)과
  // 동일 집합인 canEditField 로 연필 노출을 게이팅(서버가 최종 강제).
  const canEditSignatories = !!myRole && canEditField(myRole);
  // 개선 9건 차수1(26-08-04) §4 — 제조팀은 읽기 전용. 취소·PDF 재생성은 서버
  // WRITE_ROLES(ADMIN/FIELD_MANAGER) 뿐이라 그 집합 밖에서는 버튼을 감춘다.
  const canWrite = canWriteSignDocument(myRole);
  // 다운로드는 제조팀에도 열려 있다(서버 DOWNLOAD_ROLES 와 같은 집합).
  const canDownload = canDownloadSignDocumentPdf(myRole);

  function startSignatoryEdit(s: { id: string; name: string; phone: string }) {
    setEditTarget(s.id);
    setEditName(s.name);
    setEditPhone(s.phone);
  }

  function submitSignatoryEdit() {
    if (!editTarget || updateSignatoryMutation.isPending) return;
    updateSignatoryMutation.mutate({
      signatoryId: editTarget,
      name: editName.trim(),
      phone: editPhone.trim(),
    });
  }

  return (
    <div style={wrap}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button
          onClick={() => navigate('/field/sign/documents')}
          style={{
            fontSize: 12,
            color: 'var(--text-secondary)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          ← 목록
        </button>
        {/* sign-console-prefill — 문서 표시명(고객사_유형_YYMMDD). 양식명은 아래 '양식' 행. */}
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{doc.displayName}</h2>
        {docStatusPill(doc.status)}
        {/* 개선 9건 차수1(26-08-04) §4 — 취소는 서버 WRITE_ROLES 뿐. 제조팀에는
            렌더하지 않는다(누르면 403 인 버튼을 보여 주지 않는다). */}
        {isActive && canWrite && (
          <Button
            variant="danger"
            style={{ marginLeft: 'auto', fontSize: 12 }}
            onClick={() => setCancelTarget({ id: doc.id })}
          >
            요청 취소
          </Button>
        )}
      </div>

      {/* Document info */}
      <div style={card}>
        <div style={sectionTitle}>발송 정보</div>
        <div style={kvRow}>
          <span style={kvKey}>양식</span>
          <span>
            {doc.templateName} (v{doc.templateVersion})
          </span>
        </div>
        <div style={kvRow}>
          <span style={kvKey}>문서 유형</span>
          <span>{SIGNATURE_DOCUMENT_TYPE_LABELS[doc.documentType] ?? doc.documentType}</span>
        </div>
        <div style={kvRow}>
          <span style={kvKey}>로봇</span>
          {/* round6 §1-6(e) — 다중 로봇 전체 표기. robots 가 전체 목록(summary DTO). */}
          <span style={{ fontFamily: 'var(--font-mono)' }}>
            {doc.robots && doc.robots.length > 0 ? (
              doc.robots.map((r) => r.serial ?? r.robotId).join(', ')
            ) : doc.robotSerial ? (
              doc.robotSerial
            ) : (
              <span style={{ fontFamily: 'inherit', color: 'var(--text-secondary)' }}>미지정</span>
            )}
          </span>
          {/* 2026-09-23 — 로봇을 나중에 정하거나 바꾼다(진행 중 문서만). */}
          {isActive && canWrite && !robotEdit && (
            <Button
              variant="ghost"
              data-testid="edit-robots"
              onClick={() =>
                setRobotEdit(
                  (doc.robots ?? []).map((r) => ({
                    id: r.robotId,
                    serialShort: r.serial,
                    modelDisplay: '',
                  })),
                )
              }
            >
              {doc.robots && doc.robots.length > 0 ? '로봇 수정' : '로봇 지정'}
            </Button>
          )}
        </div>
        {robotEdit && (
          <div
            data-testid="robot-edit-panel"
            style={{
              margin: '4px 0 10px',
              padding: 12,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-card)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <RobotMultiPicker value={robotEdit} onChange={setRobotEdit} />
            <p
              style={{ margin: 0, fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}
            >
              서명 페이지와 최종 PDF에 자동으로 채워지는 로봇 시리얼과 모델에 바로 반영됩니다.
              완료된 문서는 인수증과 설치 완료가 로봇을 기준으로 만들어지기 때문에 바꿀 수 없습니다.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                onClick={() => setRobotEdit(null)}
                disabled={robotsMutation.isPending}
              >
                취소
              </Button>
              <Button
                data-testid="save-robots"
                onClick={() => robotsMutation.mutate(robotEdit.map((r) => r.id))}
                disabled={robotsMutation.isPending}
              >
                {robotsMutation.isPending ? '저장 중…' : '저장'}
              </Button>
            </div>
          </div>
        )}
        <div style={kvRow}>
          <span style={kvKey}>고객사</span>
          <span>{doc.customerName}</span>
        </div>
        <div style={kvRow}>
          <span style={kvKey}>서명 방식</span>
          <span>{doc.signOrder === 'PARALLEL' ? '동시 서명' : '순차 서명'}</span>
        </div>
        {/* 추가수정 2차(26-07-27) §2 — ISO 를 그대로 자르면 UTC 날짜라 한국
            새벽 0~9시의 발송/완료가 전날로 보였다. 상세 화면이므로 시각까지 +
            기준 라벨(결정 ②A) — 서명 완료 시각은 분쟁 시 근거가 되는 값이다. */}
        <div style={kvRow}>
          <span style={kvKey}>발송일</span>
          <span>{kstDateTime(doc.createdAt, { label: true }) ?? '–'}</span>
        </div>
        {doc.completedAt && (
          <div style={kvRow}>
            <span style={kvKey}>완료일</span>
            <span>{kstDateTime(doc.completedAt, { label: true }) ?? '–'}</span>
          </div>
        )}
      </div>

      {/* Signatories */}
      <div style={card}>
        <div style={sectionTitle}>서명자 현황</div>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-card)',
            overflow: 'hidden',
          }}
        >
          <thead>
            <tr>
              <th style={th}>슬롯</th>
              <th style={th}>이름</th>
              <th style={th}>연락처</th>
              <th style={th}>상태</th>
              <th style={th}>서명일</th>
              <th style={th}>서명 링크</th>
            </tr>
          </thead>
          <tbody>
            {doc.signatories.map((s) => {
              // sign-remote-link 트랙2(D3) — 미서명(PENDING|OPENED) 서명자만 편집.
              // 서명 완료·거부 행은 표시만(서버도 400 으로 거부).
              const editing = editTarget === s.id;
              const editableRow =
                canEditSignatories &&
                doc.status === 'IN_PROGRESS' &&
                (s.status === 'PENDING' || s.status === 'OPENED');
              return (
                <tr key={s.id}>
                  <td style={td}>
                    <span
                      style={{
                        fontSize: 11,
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {s.slot}
                    </span>
                  </td>
                  <td style={td}>
                    {editing ? (
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="이름"
                        maxLength={50}
                        autoFocus
                        aria-label="서명자 이름"
                        style={{ width: 110, fontSize: 12 }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') submitSignatoryEdit();
                          if (e.key === 'Escape') setEditTarget(null);
                        }}
                      />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span>{s.name || '–'}</span>
                        {editableRow && (
                          <button
                            type="button"
                            onClick={() => startSignatoryEdit(s)}
                            style={editIconBtn}
                            aria-label="서명자 정보 수정"
                            title="이름과 연락처 수정"
                          >
                            <Icon name="pen" size={13} aria-hidden />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {editing ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Input
                          value={editPhone}
                          onChange={(e) => setEditPhone(e.target.value)}
                          placeholder="010-0000-0000"
                          maxLength={20}
                          aria-label="서명자 연락처"
                          style={{ width: 130, fontSize: 12 }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') submitSignatoryEdit();
                            if (e.key === 'Escape') setEditTarget(null);
                          }}
                        />
                        <Button
                          onClick={submitSignatoryEdit}
                          disabled={updateSignatoryMutation.isPending}
                          style={{ fontSize: 11, padding: '3px 8px' }}
                        >
                          {updateSignatoryMutation.isPending ? '저장 중…' : '저장'}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => setEditTarget(null)}
                          disabled={updateSignatoryMutation.isPending}
                          style={{ fontSize: 11, padding: '3px 8px' }}
                        >
                          취소
                        </Button>
                      </div>
                    ) : (
                      s.phone || '–'
                    )}
                  </td>
                  <td style={td}>
                    {sigStatusPill(s.status)}
                    {/* sign-remote-link 트랙1(G4) — 거부 사유(감사 이벤트
                      metaJson.reason → declineReason 매핑). */}
                    {s.status === 'DECLINED' && s.declineReason && (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 11,
                          color: 'var(--fg-danger)',
                          maxWidth: 220,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        사유: {s.declineReason}
                      </div>
                    )}
                  </td>
                  {/* 추가수정 2차(26-07-27) §2 — KST 로 환산. 서명자 표는 줄이
                      여러 개라 날짜만(위 요약 카드가 기준 라벨을 이미 보여준다). */}
                  <td style={td}>{kstYmd(s.signedAt) ?? '–'}</td>
                  <td style={{ ...td, maxWidth: 320 }}>
                    {/* offline-signing PR-0 — signUrl 은 서명 운영 역할에게만
                      내려온다(VIEWER 등은 null → 안내 표기). */}
                    {s.status !== 'SIGNED' && s.status !== 'DECLINED' && s.signUrl ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            fontSize: 11,
                            fontFamily: 'var(--font-mono)',
                            color: 'var(--text-secondary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 220,
                          }}
                        >
                          {s.signUrl}
                        </span>
                        <button
                          onClick={() => {
                            void navigator.clipboard.writeText(s.signUrl!);
                            toast.success('링크가 복사되었습니다.');
                          }}
                          style={{
                            flexShrink: 0,
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 'var(--r-btn)',
                            border: '1px solid var(--border-default)',
                            cursor: 'pointer',
                            background: 'var(--bg-surface)',
                          }}
                        >
                          복사
                        </button>
                      </div>
                    ) : s.status !== 'SIGNED' && s.status !== 'DECLINED' ? (
                      <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                        서명 운영 권한이 필요합니다
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>–</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* sign-console-prefill-2026-07 — 미리 채우기(상시 온라인 콘솔). 진행 중 문서 +
          서명 운영 역할 + 직접 입력 항목이 있을 때만. 저장값은 서명 링크에 프리필된다. */}
      {isActive && canEditSignatories && editableFields.length > 0 && (
        <div style={card}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: prefillOpen ? 16 : 0,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ ...sectionTitle, marginBottom: 0 }}>미리 채우기</div>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              직접 입력 {prefilledFilled}/{editableFields.length} 채움
            </span>
            <Button
              variant={prefillOpen ? 'secondary' : 'primary'}
              style={{ marginLeft: 'auto', fontSize: 12 }}
              onClick={() => setPrefillOpen((v) => !v)}
            >
              {prefillOpen ? '닫기' : '미리 채우기 편집'}
            </Button>
          </div>
          {!prefillOpen && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
              계약서 그림 위에서 아는 값을 미리 채워 두면, 서명 링크를 열었을 때 그대로 채워져
              나갑니다. 사업자번호, 대표자명, 주소, 모델명처럼 시스템이 아는 값은 자동으로
              채워집니다.
            </div>
          )}
          {prefillOpen && (
            <SignDocumentPrefillPanel
              documentId={doc.id}
              fields={doc.fieldsJson}
              pageCount={doc.pageCount}
              fallbackPdfUrl={doc.pdfDownloadUrl}
              initialValues={doc.prefilledValues ?? {}}
              documentName={doc.displayName}
              onClose={() => setPrefillOpen(false)}
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!cancelTarget}
        title="서명 요청 취소"
        message="서명 요청을 취소하시겠습니까?"
        danger
        confirmLabel="취소 처리"
        busy={cancelMutation.isPending}
        onConfirm={() => cancelMutation.mutate()}
        onCancel={() => setCancelTarget(null)}
      />

      {/* 추가수정 2차(26-07-27) §4 (결정 ⑤A) — 미리 채운 PDF 다운로드.
          개선 9건 차수1(26-08-04) §4 (결정 ③A) — 게이트를 canEditSignatories 에서
          **다운로드 권한**으로 바꿨다(제조팀 포함). 여기에 '페이지 골라 받기'를 더한다.
          진행 중 문서만 — 완료/취소는 서버도 400(아래 완료본 카드가 대신 받는다). */}
      {doc.status === 'IN_PROGRESS' && canDownload && (
        <div style={card}>
          <div style={sectionTitle}>미리 채운 작성 중 PDF</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => previewPdfMutation.mutate(`(작성중) ${doc.displayName}.pdf`)}
              disabled={previewPdfMutation.isPending}
              title="지금까지 채운 값이 찍힌 PDF를 받습니다. 서명칸은 비어 있습니다."
              style={{
                fontSize: 12,
                padding: '6px 12px',
                border: '1px solid var(--border-default)',
                background: 'var(--bg-surface)',
                borderRadius: 'var(--r-btn)',
                cursor: previewPdfMutation.isPending ? 'wait' : 'pointer',
              }}
              data-testid="sign-doc-prefill-preview-download"
            >
              {previewPdfMutation.isPending ? '만드는 중…' : '문서 전체 다운로드'}
            </button>
            <button
              type="button"
              onClick={() => setPagePickerOpen((v) => !v)}
              aria-expanded={pagePickerOpen}
              title="액세서리 페이지 등 필요한 페이지만 골라 받습니다"
              style={{
                fontSize: 12,
                padding: '6px 12px',
                border: '1px solid var(--border-default)',
                background: pagePickerOpen ? 'var(--bg-sunken)' : 'var(--bg-surface)',
                color: pagePickerOpen ? 'var(--fg-brand)' : 'inherit',
                borderRadius: 'var(--r-btn)',
                cursor: 'pointer',
              }}
              data-testid="sign-doc-page-picker-toggle"
            >
              {pagePickerOpen ? '페이지 고르기 닫기' : '페이지 골라 받기'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
            글자가 벡터로 찍혀 확대해도 선명합니다. 서명·도장 칸은 비어 있고 파일명 앞에
            &quot;(작성중)&quot;이 붙어 최종본과 구분됩니다. [페이지 골라 받기]를 누르면 문서를
            페이지별로 보여 드리니 액세서리 페이지만 골라 받을 수 있습니다.
          </div>
          {pagePickerOpen && (
            <div style={{ marginTop: 14 }}>
              <SignDocumentPagePicker
                documentId={doc.id}
                variant="prefill"
                fallbackName={`(작성중) ${doc.displayName}.pdf`}
              />
            </div>
          )}
        </div>
      )}

      {/* PDF download — final stamped PDF on COMPLETED, base PDF otherwise. */}
      {doc.pdfDownloadUrl && (
        <div style={card}>
          <div style={sectionTitle}>
            {doc.status === 'COMPLETED' ? '서명 완료 PDF' : '서명 전 베이스 PDF'}
          </div>
          <a
            href={doc.pdfDownloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, color: 'var(--fg-brand)', textDecoration: 'underline' }}
          >
            {doc.status === 'COMPLETED'
              ? '입력값, 서명, 인증 페이지가 모두 들어간 최종 PDF 다운로드'
              : '베이스 PDF 미리보기'}
          </a>
          {/* 개선 9건 차수1(26-08-04) §4 — '완료본도' 오너 결정. 완료된 문서의
              최종 PDF 에도 같은 페이지 고르기를 붙인다(진행 중과 동일한 컴포넌트,
              대상 문서만 final). 재고팀이 서명 끝난 인수증의 액세서리 페이지를
              받는 실제 동선이 이쪽이다. */}
          {doc.status === 'COMPLETED' && canDownload && (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setPagePickerOpen((v) => !v)}
                aria-expanded={pagePickerOpen}
                title="액세서리 페이지 등 필요한 페이지만 골라 받습니다"
                style={{
                  fontSize: 12,
                  padding: '6px 12px',
                  border: '1px solid var(--border-default)',
                  background: pagePickerOpen ? 'var(--bg-sunken)' : 'var(--bg-surface)',
                  color: pagePickerOpen ? 'var(--fg-brand)' : 'inherit',
                  borderRadius: 'var(--r-btn)',
                  cursor: 'pointer',
                }}
                data-testid="sign-doc-final-page-picker-toggle"
              >
                {pagePickerOpen ? '페이지 고르기 닫기' : '페이지 골라 받기'}
              </button>
              {pagePickerOpen && (
                <div style={{ marginTop: 14 }}>
                  <SignDocumentPagePicker
                    documentId={doc.id}
                    variant="final"
                    fallbackName={`${doc.displayName}.pdf`}
                  />
                </div>
              )}
            </div>
          )}
          {/* PDF 재생성은 서버 WRITE_ROLES 뿐 — 제조팀에는 감춘다. */}
          {doc.status === 'COMPLETED' && canWrite && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                onClick={() => refinalizeMutation.mutate()}
                disabled={refinalizeMutation.isPending}
                title="최종 PDF나 인수증, 설치 연동이 빠졌을 때 다시 처리합니다. 여러 번 눌러도 괜찮습니다."
                style={{
                  fontSize: 12,
                  padding: '4px 10px',
                  border: '1px solid var(--border-default)',
                  background: 'var(--bg-surface)',
                  borderRadius: 'var(--r-btn)',
                  cursor: 'pointer',
                }}
                data-testid="sign-doc-refinalize"
              >
                {refinalizeMutation.isPending ? '재처리 중…' : 'PDF 재생성 / 후처리 재실행'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
